import { z } from "zod";

import { basicsSchema, defaultBasics } from "./basics";
import { defaultMetadata, metadataSchema } from "./metadata";
import { defaultSections, sectionsSchema } from "./sections";

// Schema
export const resumeDataSchema = z.object({
  basics: basicsSchema,
  sections: sectionsSchema,
  metadata: metadataSchema,
});

// Type
export type ResumeData = z.infer<typeof resumeDataSchema>;

// Defaults
export const defaultResumeData: ResumeData = {
  basics: defaultBasics,
  sections: defaultSections,
  metadata: defaultMetadata,
};

export const userSettingsSchema = z.object({
  density: z.enum(["comfortable", "compact"]).default("comfortable"),
  theme: z.enum(["system", "light", "dark"]).default("system"),
  sidebarCollapsed: z.boolean().default(false),
});

export type UserSettings = z.infer<typeof userSettingsSchema>;

export const ONBOARDING_STEPS = ["welcome", "import", "profile", "targets", "ready"] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

// ─── Early-career beachhead (Spec 038) ───────────────────────────────────
// Explicit experience-level answer captured by the early-career onboarding
// branch. Additive + optional.
export const EARLY_CAREER_EXPERIENCE_LEVELS = ["student", "0-3", "3-7", "7+"] as const;
export type EarlyCareerExperienceLevel = (typeof EARLY_CAREER_EXPERIENCE_LEVELS)[number];

/** The levels that branch the onboarding into the early-career path. */
export const EARLY_CAREER_LEVELS: readonly EarlyCareerExperienceLevel[] = ["student", "0-3"];

/** True when the answered experience level should trigger early-career adaptations. */
export const isEarlyCareerLevel = (
  level: string | undefined | null,
): level is EarlyCareerExperienceLevel =>
  level != null && (EARLY_CAREER_LEVELS as readonly string[]).includes(level);

export const onboardingStateSchema = z.object({
  step: z.enum(ONBOARDING_STEPS).default("welcome"),
  importChoice: z.enum(["upload", "linkedin", "url", "scratch"]).optional(),
  resumeFileId: z.string().optional(),
  linkedinUrl: z.string().url().optional(),
  roles: z.array(z.string()).default([]),
  yearsOfExperience: z.string().optional(),
  seniority: z.string().optional(),
  // Spec 038 — explicit early-career experience-level answer (additive, optional).
  earlyCareerExperienceLevel: z.enum(EARLY_CAREER_EXPERIENCE_LEVELS).optional(),
  // A skipped answer must not be inferred later from imported work history.
  experienceQuestionSkipped: z.boolean().optional(),
  skills: z.array(z.string()).default([]),
  salaryMin: z.number().int().min(40_000).optional(),
  salaryMax: z.number().int().max(500_000).optional(),
  workArrangements: z.array(z.enum(["Remote", "Hybrid", "Onsite"])).default([]),
  locations: z.array(z.string()).default([]),
  visaSponsorship: z.boolean().optional(),
  companySizes: z.array(z.string()).default([]),
  selectedTier: z.enum(["free", "pro"]).optional(),
});

export type OnboardingState = z.infer<typeof onboardingStateSchema>;

export * from "./basics";
export * from "./billing";
export * from "./extension-claim";
export * from "./job-batch";
export * from "./metadata";
export * from "./resume";
export * from "./sections";
export * from "./shared";
